/**
 * 主题切换组件
 * 提供明暗模式切换功能，支持状态持久化和平滑过渡动画
 */

import { MoonOutlined,SunOutlined } from '@ant-design/icons'
import { Button,Tooltip } from 'antd'
import React,{ useEffect,useState } from 'react'
import {
ThemeMode,
applyThemeToDocument,
getNextTheme,
getStoredTheme,
isDarkTheme,
saveThemeToStorage,
subscribeSystemThemeChange,
} from './themeUtils'

// 主题切换组件属性接口
export interface ThemeToggleProps {
  /** 当前主题模式 */
  theme?: ThemeMode
  /** 主题切换回调函数 */
  onChange?: (theme: ThemeMode) => void
  /** 组件大小 */
  size?: 'small' | 'middle' | 'large'
  /** 是否显示文本 */
  showText?: boolean
  /** 自定义样式类名 */
  className?: string
  /** 是否禁用 */
  disabled?: boolean
}

interface ThemeDisplayInfo {
  icon: React.ReactNode
  text: string
  tooltip: string
}

const getThemeInfo = (theme: ThemeMode): ThemeDisplayInfo => {
  switch (theme) {
    case ThemeMode.LIGHT:
      return {
        icon: <SunOutlined />,
        text: '浅色模式',
        tooltip: '切换到深色模式',
      }
    case ThemeMode.DARK:
      return {
        icon: <MoonOutlined />,
        text: '深色模式',
        tooltip: '切换到自动模式',
      }
    case ThemeMode.AUTO:
      return {
        icon: isDarkTheme(theme) ? <MoonOutlined /> : <SunOutlined />,
        text: '自动模式',
        tooltip: '切换到浅色模式',
      }
    default:
      return {
        icon: <SunOutlined />,
        text: '浅色模式',
        tooltip: '切换主题',
      }
  }
}

/**
 * 主题切换组件
 */
const ThemeToggle: React.FC<ThemeToggleProps> = ({
  theme: controlledTheme,
  onChange,
  size = 'middle',
  showText = false,
  className = '',
  disabled = false,
}) => {
  // 内部主题状态
  const [internalTheme, setInternalTheme] = useState<ThemeMode>(() => {
    return controlledTheme || getStoredTheme()
  })

  // 切换动画状态
  const [isSwitching, setIsSwitching] = useState(false)

  // 使用受控或非受控模式
  const currentTheme = controlledTheme !== undefined ? controlledTheme : internalTheme

  // 监听系统主题变化
  useEffect(() => {
    return subscribeSystemThemeChange(currentTheme, () => applyThemeToDocument(ThemeMode.AUTO))
  }, [currentTheme])

  // 初始化时应用主题
  useEffect(() => {
    applyThemeToDocument(currentTheme)
  }, [currentTheme])

  /**
   * 处理主题切换
   */
  const handleThemeChange = () => {
    if (disabled || isSwitching) return

    // 开始切换动画
    setIsSwitching(true)

    const nextTheme = getNextTheme(currentTheme)

    // WHY: 先保留当前主题 200ms，让按钮 loading/过渡类先出现，避免视觉状态和 DOM 类同步跳变。
    setTimeout(() => {
      // 更新状态
      if (controlledTheme === undefined) {
        setInternalTheme(nextTheme)
        saveThemeToStorage(nextTheme)
      }

      // 触发回调
      onChange?.(nextTheme)

      // 结束切换动画
      setTimeout(() => {
        setIsSwitching(false)
      }, 100)
    }, 200)
  }

  const themeInfo = getThemeInfo(currentTheme)

  return (
    <Tooltip title={themeInfo.tooltip} placement='bottom'>
      <Button
        type='text'
        size={size}
        icon={themeInfo.icon}
        onClick={handleThemeChange}
        disabled={disabled || isSwitching}
        className={`theme-toggle ${isSwitching ? 'switching' : ''} ${className}`}
        aria-label={`当前主题: ${themeInfo.text}, 点击${themeInfo.tooltip}`}
        loading={isSwitching}
      >
        {showText && themeInfo.text}
      </Button>
    </Tooltip>
  )
}

export default ThemeToggle
